import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { ACTIVITY_KINDS, ACTIVITY_KIND_LABEL, DATA_STATUS, DATA_STATUS_LABEL, EF_SCOPES, STAGES, STAGE_LABEL, type EfScope, type ElectricitySource, type EmissionFactor, type Technology } from "@h2lca/core";
import { Button, Checkbox, NumberField, SelectField, TextArea, TextField } from "@/components/ui/FormControls";
import { useMaster } from "@/store/master";

/**
 * 배출계수,전력원,대상기술은 칸이 적어 폼으로 고친다. 값은 문자열로 들고 있다가 제출할 때 숫자로 바꾼다.
 * 스키마 검증(zod)은 부모가 한다 , 여기서는 모양만 만든다.
 */
interface FormProps {
  initial: Record<string, unknown>;
  /** 기존 항목(또는 내장 기본값)을 고칠 때는 키를 못 바꾼다 */
  lockKey: boolean;
  busy: boolean;
  onSubmit: (obj: Record<string, unknown>) => void;
  onCancel: () => void;
}

const SCOPE_LABEL: Record<EfScope, string> = { general: "일반 LCA 전용", cert: "인증제 전용", both: "둘 다" };

function Actions({ busy, onCancel }: { busy: boolean; onCancel: () => void }) {
  return (
    <div className="form-actions">
      <Button type="submit" variant="primary" disabled={busy}>
        {busy ? "저장 중…" : "저장"}
      </Button>
      <Button variant="secondary" onClick={onCancel} disabled={busy}>취소</Button>
    </div>
  );
}

function Wrap({ children, onSubmit }: { children: ReactNode; onSubmit: (e: FormEvent) => void }) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-content" noValidate>
      {children}
    </form>
  );
}

const str = (v: unknown, d = "") => (v === undefined || v === null ? d : String(v));
const num = (s: string) => (s.trim() === "" ? Number.NaN : Number(s));

/* ── 배출계수 ─────────────────────────────────────────────────── */
export function FactorForm({ initial, lockKey, busy, onSubmit, onCancel }: FormProps) {
  const f = initial as Partial<EmissionFactor>;
  const [d, setD] = useState({
    id: str(f.id), name: str(f.name), kind: str(f.kind, "electricity"), value: str(f.value), unit: str(f.unit, "kgCO2eq/"), source: str(f.source), sourceUrl: str(f.sourceUrl),
    baseYear: str(f.baseYear, String(new Date().getFullYear())), scope: str(f.scope, "both"), gwpBasis: str(f.gwpBasis, "IPCC AR5 100년"), status: str(f.status, "placeholder"),
    isDefault: f.isDefault ?? false, note: str(f.note), active: f.active ?? true,
  });
  const set = <K extends keyof typeof d>(k: K, v: (typeof d)[K]) => setD((s) => ({ ...s, [k]: v }));

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      id: d.id.trim(), name: d.name.trim(), kind: d.kind, value: num(d.value), unit: d.unit.trim(), source: d.source.trim(), sourceUrl: d.sourceUrl.trim() || undefined,
      baseYear: num(d.baseYear), scope: d.scope, gwpBasis: d.gwpBasis.trim(), status: d.status, isDefault: d.isDefault, note: d.note.trim() || undefined, active: d.active,
    });
  }

  return (
    <Wrap onSubmit={submit}>
      <div className="form-grid">
        <TextField label="ID" value={d.id} readOnly={lockKey} onChange={(e) => set("id", e.target.value)} hint={lockKey ? "키는 바꿀 수 없습니다" : "예: ef.electricity.grid_kr. 저장 뒤에는 못 바꿉니다"} />
        <TextField label="이름" value={d.name} onChange={(e) => set("name", e.target.value)} />
        <SelectField label="활동 종류" value={d.kind} onChange={(e) => set("kind", e.target.value)}>
          {ACTIVITY_KINDS.map((k) => (
            <option key={k} value={k}>{ACTIVITY_KIND_LABEL[k]}</option>
          ))}
        </SelectField>
        <NumberField label="값" unit={d.unit || "kgCO2eq/…"} step="any" min={0} value={d.value} onChange={(e) => set("value", e.target.value)} />
        <TextField label="단위" value={d.unit} onChange={(e) => set("unit", e.target.value)} hint="kgCO2eq/<활동자료 단위>. kWh, kg, L, MJ 등" />
        <NumberField label="기준연도" unit="년" step={1} value={d.baseYear} onChange={(e) => set("baseYear", e.target.value)} />
        <div className="form-span">
        <TextField label="출처" value={d.source} onChange={(e) => set("source", e.target.value)} hint="기관, 문서, 표 번호까지 입력합니다. 화면과 보고서에 그대로 나갑니다" />
        </div>
        <div className="form-span">
          <TextField label="출처 URL(선택)" type="url" value={d.sourceUrl} onChange={(e) => set("sourceUrl", e.target.value)} />
        </div>
        <SelectField label="적용 범위" value={d.scope} onChange={(e) => set("scope", e.target.value)}>
          {EF_SCOPES.map((s) => (
            <option key={s} value={s}>{SCOPE_LABEL[s]}</option>
          ))}
        </SelectField>
        <TextField label="GWP 기준" value={d.gwpBasis} onChange={(e) => set("gwpBasis", e.target.value)} />
        <SelectField label="자료 상태" value={d.status} onChange={(e) => set("status", e.target.value)}>
          {DATA_STATUS.map((s) => (
            <option key={s} value={s}>{DATA_STATUS_LABEL[s]}</option>
          ))}
        </SelectField>
        <div className="flex flex-col justify-end">
          <Checkbox label="같은 활동 종류의 기본 계수" checked={d.isDefault} onChange={(e) => set("isDefault", e.target.checked)} description="설비 입력에 자동으로 붙는 계수" />
          <Checkbox label="사용" checked={d.active} onChange={(e) => set("active", e.target.checked)} />
        </div>
        <div className="form-span">
          <TextArea label="비고(선택)" rows={3} value={d.note} onChange={(e) => set("note", e.target.value)} />
        </div>
      </div>
      <Actions busy={busy} onCancel={onCancel} />
    </Wrap>
  );
}

/* ── 전력원 ───────────────────────────────────────────────────── */
export function SourceForm({ initial, lockKey, busy, onSubmit, onCancel }: FormProps) {
  const s = initial as Partial<ElectricitySource>;
  const factors = useMaster((st) => st.data.factors);
  const [d, setD] = useState({ id: str(s.id), label: str(s.label), efId: str(s.efId), description: str(s.description), certRenewable: s.certRenewable ?? false, active: s.active ?? true });
  const set = <K extends keyof typeof d>(k: K, v: (typeof d)[K]) => setD((st) => ({ ...st, [k]: v }));

  // 전력 배출계수만 고르되, 목록에 없는 값(이미 저장된 것)도 잃지 않는다
  const options = useMemo(() => {
    const list = factors.filter((f) => f.kind === "electricity").map((f) => ({ id: f.id, name: f.name }));
    if (d.efId && !list.some((o) => o.id === d.efId)) list.push({ id: d.efId, name: `${d.efId} (목록에 없음)` });
    return list;
  }, [factors, d.efId]);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({ id: d.id.trim(), label: d.label.trim(), efId: d.efId, description: d.description.trim(), certRenewable: d.certRenewable, active: d.active });
  }

  return (
    <Wrap onSubmit={submit}>
      <div className="form-grid">
        <TextField label="ID" value={d.id} readOnly={lockKey} onChange={(e) => set("id", e.target.value)} hint={lockKey ? "키는 바꿀 수 없습니다" : "예: grid_kr"} />
        <TextField label="이름" value={d.label} onChange={(e) => set("label", e.target.value)} />
        <div className="form-span">
          <SelectField label="배출계수(전력)" value={d.efId} onChange={(e) => set("efId", e.target.value)} hint="kind=electricity 인 배출계수">
            <option value="">선택하지 않음</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>{o.name}, {o.id}</option>
            ))}
          </SelectField>
        </div>
        <div className="form-span">
          <TextArea label="설명" rows={3} value={d.description} onChange={(e) => set("description", e.target.value)} />
        </div>
        <Checkbox label="청정수소 인증제에서 재생에너지로 인정" checked={d.certRenewable} onChange={(e) => set("certRenewable", e.target.checked)} description="교육용 단순화. 인증제 모의산정에서 배출계수 0 취급" />
        <Checkbox label="사용" checked={d.active} onChange={(e) => set("active", e.target.checked)} />
      </div>
      <Actions busy={busy} onCancel={onCancel} />
    </Wrap>
  );
}

/* ── 대상기술 ─────────────────────────────────────────────────── */
export function TechnologyForm({ initial, lockKey, busy, onSubmit, onCancel }: FormProps) {
  const t = initial as Partial<Technology>;
  const [d, setD] = useState({ id: str(t.id), stage: str(t.stage, "production"), name: str(t.name), description: str(t.description), status: str(t.status, "ready"), order: str(t.order, "0"), active: t.active ?? true });
  const set = <K extends keyof typeof d>(k: K, v: (typeof d)[K]) => setD((st) => ({ ...st, [k]: v }));

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({ id: d.id.trim(), stage: d.stage, name: d.name.trim(), description: d.description.trim(), status: d.status, order: num(d.order), active: d.active });
  }

  return (
    <Wrap onSubmit={submit}>
      <div className="form-grid">
        <TextField label="ID" value={d.id} readOnly={lockKey} onChange={(e) => set("id", e.target.value)} hint={lockKey ? "키는 바꿀 수 없습니다" : "예: pem"} />
        <TextField label="이름" value={d.name} onChange={(e) => set("name", e.target.value)} />
        <SelectField label="단계" value={d.stage} onChange={(e) => set("stage", e.target.value)}>
          {STAGES.map((s) => (
            <option key={s} value={s}>{STAGE_LABEL[s]}</option>
          ))}
        </SelectField>
        <SelectField label="상태" value={d.status} onChange={(e) => set("status", e.target.value)} hint="자리만: 팔레트에 보이되 값은 협의 후 채움">
          <option value="ready">준비됨</option>
          <option value="placeholder">자리만(협의 후 채움)</option>
        </SelectField>
        <NumberField label="순서" unit="번째" step={1} value={d.order} onChange={(e) => set("order", e.target.value)} />
        <div className="flex flex-col justify-end">
          <Checkbox label="사용" checked={d.active} onChange={(e) => set("active", e.target.checked)} />
        </div>
        <div className="form-span">
          <TextArea label="설명" rows={3} value={d.description} onChange={(e) => set("description", e.target.value)} />
        </div>
      </div>
      <Actions busy={busy} onCancel={onCancel} />
    </Wrap>
  );
}
